<script setup>
import useVuelidate from "@vuelidate/core";
import { helpers, maxLength, required, requiredIf } from "@vuelidate/validators";
import { isFuture } from "date-fns";
import _ from "lodash";
import { computed, onMounted, reactive, ref, toRefs, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import Checkbox from "@/components/forms/SharedComponents/DarkBlueInputs/Checkbox.vue";
import DateField from "@/components/forms/SharedComponents/DarkBlueInputs/DateField.vue";
import SingleSelect from "@/components/forms/SharedComponents/DarkBlueInputs/SingleSelect.vue";
import TextField from "@/components/forms/SharedComponents/DarkBlueInputs/TextField.vue";
import { patchFormioObject } from "@/components/RegistrationProcess/utils/utils";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import { GENDER_IDENTITIES, GENDERS, GENDERSV3 } from "@/constants.js";
import { minDateValidator } from "@/mixins/customValidators";
import countriesService from "@/services/countries.js";
import formService from "@/services/form.js";
import { useInitialOptionsData } from "../composables/useInitialOptionsData";

const props = defineProps({
  // see useApplication.js for application definition
  application: {
    type: Object,
    default: () => {},
    required: true,
  },
  goToNextStep: {
    type: Function,
    default: () => {},
    required: true,
  },
});

// Variables
const router = useRouter();
const route = useRoute();
const store = useStore();
const { initialData: initialOptionsData } = useInitialOptionsData();
const aboutYouSubmit = ref(null);
const form = reactive({
  legalFirstName: "", // required
  preferredFirstName: "",
  middleName: "",
  lastName: "", //required
  birthdate: "", // required
  gender: null, // required
  genderIdentity: null,
  otherGenderIdentity: null,
  pronouns: "",
  citizenship: null, // required
  imDualCitizen: false,
  citizenship2: null, // required if imDualCitizen is true
});
const { application } = toRefs(props);
const { upsertApplicationOrderAndSubmission, profile, primaryOrder, session, updateProfile } = application.value;
const initialCountries = computed(() => initialOptionsData?.value?.countries);
const countries = ref([]); // Gets data from countries service
const requiredErrorCopy = "This field is required.";
const dateErrorCopy = "Invalid birthdate. Please use calendar.";
const networkError = ref(undefined);
const notFutureDate = (value) => !isFuture(new Date(value));

// TODO: Works while impersonating?
const student_formio_submission_id = computed(() => {
  return store.state.currentUser?.formioStudentId ?? "";
});

const oktaId = computed(() => {
  return store.state.currentUser?.participantId ?? "";
});

const currentUser = computed(() => {
  return store.state.currentUser;
});

const formioGenderFormat = computed(() => {
  if (form.gender?.value === "Non-Binary") return "nonBinary";
  return GENDERS.find((gender) => gender.label === form.gender?.label)?.value ?? "";
});

const formioPatchStudentObject = computed(() => {
  // Updates student submission
  return [
    patchFormioObject("gender", formioGenderFormat.value),
    patchFormioObject("citizenship", form.citizenship.name),
    patchFormioObject("ihavedualcitizenship", form.imDualCitizen),
  ];
});

const saveObject = computed(() => {
  // Object to update profile service
  return {
    email: currentUser.value.email,
    legal_first_name: form.legalFirstName,
    preferred_first_name: form.preferredFirstName,
    middle_name: form.middleName,
    last_name: form.lastName,
    birthdate: form.birthdate,
    gender_identity: form.gender.label, // gender on passport
    pronouns: form.pronouns,
    primary_citizenship_country_id: form.citizenship?.id ?? null,
    dual_citizenship: form.imDualCitizen,
    dual_citizenship_country_first_id: form.citizenship2?.id ?? null,
    majors: profile.value?.majors ?? [],
    minors: profile.value?.minors ?? [],
    colleges: profile.value?.colleges ?? [],
    languages: profile.value?.languages ?? [],
    parents: profile.value?.parents ?? [],
    user_types: profile.value?.user_types ?? [],
    okta_id: profile?.value?.okta_id,
    gender_identity_id: form.genderIdentity?.id ?? null,
    other_gender: form.otherGenderIdentity,
  };
});

// Form validation rules (Vuelidate library)
const rules = {
  legalFirstName: {
    required: helpers.withMessage(requiredErrorCopy, required),
    maxLength: helpers.withMessage(
      ({ $params }) => `Legal First Name must have fewer than ${$params.max} characters.`,
      maxLength(100),
    ),
  },
  preferredFirstName: {
    maxLength: helpers.withMessage(
      ({ $params }) => `Preferred First Name must have fewer than ${$params.max} characters.`,
      maxLength(100),
    ),
  },
  middleName: {
    maxLength: helpers.withMessage(
      ({ $params }) => `Middle Name must have fewer than ${$params.max} characters.`,
      maxLength(100),
    ),
  },
  lastName: {
    required: helpers.withMessage(requiredErrorCopy, required),
    maxLength: helpers.withMessage(
      ({ $params }) => `Last Name must have fewer than ${$params.max} characters.`,
      maxLength(100),
    ),
  },
  birthdate: {
    required: helpers.withMessage(requiredErrorCopy, required),
    isValidMinDate: helpers.withMessage(dateErrorCopy, minDateValidator("1940")),
    notFutureDate: helpers.withMessage(dateErrorCopy, notFutureDate),
  },
  gender: {
    required: helpers.withMessage(requiredErrorCopy, required),
  },
  genderIdentity: {},
  otherGenderIdentity: {
    maxLength: helpers.withMessage(
      ({ $params }) => `Gender Identity must have fewer than ${$params.max} characters.`,
      maxLength(100),
    ),
  },

  pronouns: {
    maxLength: helpers.withMessage(
      ({ $params }) => `Pronouns must have fewer than ${$params.max} characters.`,
      maxLength(100),
    ),
  },
  citizenship: {
    required: helpers.withMessage(requiredErrorCopy, required),
  },
  imDualCitizen: {},
  citizenship2: {
    required: helpers.withMessage(
      requiredErrorCopy,
      requiredIf(() => form.imDualCitizen),
    ),
  },
};
const v$ = useVuelidate(rules, form);

onMounted(async () => {
  countries.value = initialCountries?.value ?? [];
  prePopulateProfileData();
  await canViewForm();
});

// Methods
async function canViewForm() {
  // Can view form if is authenticated
  const canViewForm = store.getters.getCurrentUser;
  if (!canViewForm) {
    // Go to applications
    router.push({ name: "applications" });
  }
}

async function getCountries({ search, loading }) {
  // Get Countries data
  const { data } = await countriesService.searchCountries({
    limit: 20,
    q: search ?? "",
  });
  loading(false);

  const filteredCountries = data?.data?.filter((country) => country.name !== "United States") || [];

  countries.value = filteredCountries;
}

const preFilledCitizenship = computed(() =>
  reorderedCountries.value?.find((each) => each.id === profile.value?.primary_citizenship_country_id),
);

const preFilledCitizenship2 = computed(() =>
  reorderedCountries.value?.find((each) => each.id === profile.value?.dual_citizenship_country_first_id),
);

const reorderedCountries = computed(() => {
  const reordered = [...countries.value];

  const unitedStates = {
    archived_at: null,
    created_at: "2021-10-14T14:22:08Z",
    id: "US",
    iso: "US",
    iso3: "USA",
    latitude: 38,
    longitude: -97,
    name: "United States",
    numeric_code: 840,
    updated_at: null,
    updated_by: null,
  };
  reordered.unshift(unitedStates);

  return reordered;
});

async function submitAboutYou() {
  if (aboutYouSubmit.value) {
    aboutYouSubmit.value.startLoading();
  }
  // TODO: What to do with existing accounts w/o profiles?
  try {
    const promises = [updateProfile(oktaId.value, saveObject.value)];

    // Update student submission - for gender, citizenships
    if (student_formio_submission_id.value) {
      promises.push(
        formService.partiallyUpdateSubmission(
          "student",
          student_formio_submission_id.value,
          formioPatchStudentObject.value,
        ),
      );
    }

    // possibly this was already done during handoff from configurator,
    // but all operations in here are idempotent so better safe than sorry
    promises.push(upsertApplicationOrderAndSubmission());

    try {
      await Promise.all(promises); // allSettled not appropriate here because we DO want entire operation to fail if one part does
    } catch (e) {
      // one network operation failing here puts student application data in an inconsistent state (e.g. order exists without form.io programselection);
      // user will be prompted to reach out to support to resolve
      networkError.value = e;
      return;
    }

    if (application.value?.applicationId?.value) {
      await router.replace({
        name: "modify-application",
        params: {
          applicationId: application.value.applicationId.value,
          stage: "apply",
          step: route.params?.step,
        },
        query: route.query,
      });
    }

    props.goToNextStep();
  } finally {
    if (aboutYouSubmit.value) {
      aboutYouSubmit.value.stopLoading();
    }
  }
}

const prePopulateProfileData = async () => {
  if (!_.isEmpty(profile.value)) {
    // Gets data of profile, pre-populates fields
    form.legalFirstName = profile.value?.legal_first_name ?? "";
    form.preferredFirstName = profile.value?.preferred_first_name ?? "";
    form.middleName = profile.value?.middle_name ?? "";
    form.lastName = profile.value?.last_name ?? "";
    form.birthdate = profile.value?.birthdate ?? "";
    form.gender = profile.value?.gender_identity
      ? GENDERSV3.find((gender) => gender.label === profile.value.gender_identity)
      : null;
    form.pronouns = profile.value?.pronouns ?? "";
    form.imDualCitizen = profile.value?.dual_citizenship ?? false;
    form.genderIdentity = GENDER_IDENTITIES.find((identity) => identity.id === profile.value?.gender_identity_id);
    form.otherGenderIdentity = profile.value?.other_gender;
  }

  form.citizenship = preFilledCitizenship.value ?? {};
  form.citizenship2 = preFilledCitizenship2.value ?? {};
};

onMounted(prePopulateProfileData);

watch(profile, (_, old) => {
  if (!old) {
    prePopulateProfileData();
  }
});

const dualCitizenshipOptions = computed(() => {
  if (form.citizenship) {
    return reorderedCountries.value.filter((country) => country.id !== form.citizenship.id);
  }
  return reorderedCountries.value;
});
</script>

<template>
  <div class="text-indigo-base text-left bg-white w-full px-4 pb-4 border-collapse md:mt-0 md:p-0">
    <div class="grid grid-cols-1 gap-6 md:gap-7 md:pt-4">
      <div>
        <h1 class="font-bold text-[28px] uppercase">
          About You
        </h1>
        <p class="font-medium text-xs">
          With the exception of pronouns, please ensure all information matches your passport.
        </p>
      </div>
      <p class="font-medium text-xs">
        Fields marked with (*) are required.
      </p>
      <!-- Legal First Name* -->
      <TextField
        id="legalFirstName"
        v-model.trim="form.legalFirstName"
        label="Legal First Name*"
        placeholder="Enter Your Legal First Name"
        :vuelidate-field="v$.legalFirstName"
        :show-errors="true"
        label-test-id="legal-first-name-label"
      />
      <TextField
        id="preferredFirstName"
        v-model.trim="form.preferredFirstName"
        label="Preferred First Name"
        placeholder="Enter Your  Preferred First Name"
        :vuelidate-field="v$.preferredFirstName"
        :show-errors="true"
        label-test-id="preferred-first-name-label"
      />

      <!-- Middle Name -->
      <TextField
        id="middleName"
        v-model.trim="form.middleName"
        label="Middle Name"
        placeholder="Enter Your Middle Name"
        :vuelidate-field="v$.middleName"
        :show-errors="true"
      >
        <template #help-content>
          <p class="text-sm">
            Required if you have a middle name on your passport.
          </p>
        </template>
      </TextField>

      <!-- Last Name* -->
      <TextField
        id="lastName"
        v-model.trim="form.lastName"
        label="Last Name*"
        placeholder="Enter Your Last Name"
        :vuelidate-field="v$.lastName"
        :show-errors="true"
      />

      <!-- Birthdate -->
      <DateField id="birthdate" v-model.trim="form.birthdate" label="Birthdate*" :vuelidate-field="v$.birthdate">
        <template #help-content>
          <p class="text-sm">
            For eligibility for programs with age restrictions.
          </p>
        </template>
      </DateField>

      <!-- Gender -->
      <SingleSelect
        id="gender"
        v-model.trim="form.gender"
        label="Gender on Passport*"
        :vuelidate-field="v$.gender"
        :options="GENDERSV3"
        option-label="label"
      />

      <!-- Gender Identity -->
      <SingleSelect
        id="genderIdentity"
        v-model.trim="form.genderIdentity"
        test-id="-gender-identity-field"
        label="Which most closely describes your gender?"
        :vuelidate-field="v$.genderIdentity"
        :options="GENDER_IDENTITIES"
        option-label="label"
      />

      <!-- Additional Gender Identity Field -->
      <TextField
        v-if="form.genderIdentity?.value === 'other'"
        id="otherGenderIdentity"
        v-model.trim="form.otherGenderIdentity"
        data-testid="additional-gender-identity-field"
        label="Please Specify Your Gender"
        placeholder="Enter Your Gender Identity"
        :vuelidate-field="v$.otherGenderIdentity"
        :show-errors="true"
      />

      <!-- Gender Pronouns -->
      <TextField
        id="pronouns"
        v-model.trim="form.pronouns"
        label="Gender Pronouns"
        placeholder="Enter Your Pronouns"
        :vuelidate-field="v$.pronouns"
        :show-errors="true"
      />

      <!-- Citizenship -->
      <SingleSelect
        id="citizenship"
        v-model.trim="form.citizenship"
        label="Citizenship*"
        :vuelidate-field="v$.citizenship"
        :options="reorderedCountries"
        :search-method="true"
        option-label="name"
        @search="getCountries"
      />

      <!-- I have dual citizenship -->
      <Checkbox id="dualcitizen" v-model.trim="v$.imDualCitizen.$model" :vuelidate-field="v$.imDualCitizen">
        <template #label>
          I am a dual citizen.
        </template>
      </Checkbox>

      <!-- Citizenship 2nd (dual) -->
      <SingleSelect
        v-if="form.imDualCitizen"
        id="citizenship2"
        v-model.trim="form.citizenship2"
        label="Dual Citizenship*"
        :vuelidate-field="v$.citizenship2"
        :options="dualCitizenshipOptions"
        :search-method="true"
        option-label="name"
        @search="getCountries"
      />
      <p v-if="Boolean(networkError)" class="error text-error-900">We're sorry, something went wrong. Please contact <a href="mailto:productsupport@apiexperience.com">productsupport@apiexperience.com</a>.</p>
      <ButtonWithSpinner
        id="aboutYouButton"
        ref="aboutYouSubmit"
        data-cy="aboutYouButton"
        data-testid="about-you-button"
        type="submit"
        variant="secondary"
        variant-type="block"
        button-height="min-h-[58px] md:min-h-[60px] mt-4"
        :grey-disabled-class="true"
        :disabled="v$.$invalid || Boolean(networkError)"
        @click="submitAboutYou"
      >
        <div class="flex items-center justify-center">
          <span class="pr-3 uppercase">
            Continue
          </span>
          <ArrowRight custom-class="w-4 h-4" />
        </div>
      </ButtonWithSpinner>
    </div>
  </div>
</template>
